
import { useState, useEffect } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { ComplaintFormValues } from "../types";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { useTheme } from "@/context/ThemeContext";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useSupportVehicleLookup } from "@/hooks/useSupportVehicleLookup";
import { supportLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

interface RegistrationStepProps {
  form: UseFormReturn<ComplaintFormValues>;
}

export const RegistrationStep = ({ form }: RegistrationStepProps) => {
  const [isValidating, setIsValidating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [validationResult, setValidationResult] = useState<{ isValid: boolean; error?: string }>({ isValid: false });
  const { baseText, subtleText } = useTextColorClass();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const labels = supportLabels(getLanguage());
  const { lookupComplaintVehicleAsync, isLookingUp } = useSupportVehicleLookup();

  const validateRegistrationNumber = (value: string): { isValid: boolean; error?: string } => {
    if (!value.trim()) {
      return { isValid: false };
    }

    const cleanValue = value.replace(/\s/g, '').toUpperCase();

    if (cleanValue.length > 7) {
      return { isValid: false, error: labels.registrationInvalidLength ?? 'Registreringsnummer är för långt' };
    }

    const patterns = [
      /^[A-Z]{3}\d{3}$/,
      /^[A-Z]{3}\d{2}[A-Z]$/,
      /^[A-Z]{2}\d{5}$/,
      /^[A-Z]{3}-\d{3}$/,
      /^[A-Z]{3}\d{3}$/,
      /^[A-Z]{2}\d{4}[A-Z]$/,
      /^[A-Z]\d{5}[A-Z]$/,
    ];

    const isValid = patterns.some(pattern => pattern.test(cleanValue));

    if (!isValid) {
      return { isValid: false, error: labels.registrationInvalidFormat ?? 'Ogiltigt format för registreringsnummer' };
    }

    return { isValid: true };
  };

  useEffect(() => {
    const regNumber = form.watch('registrationNumber');

    if (regNumber) {
      setIsValidating(true);
      form.clearErrors('registrationNumber');

      const timeoutId = setTimeout(() => {
        const result = validateRegistrationNumber(regNumber);
        setValidationResult(result);
        setIsValidating(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setValidationResult({ isValid: false });
      setIsValidating(false);
    }
  }, [form.watch('registrationNumber')]);

  const handleSearch = async (): Promise<boolean> => {
    const regNumber = form.getValues('registrationNumber');
    if (!validationResult.isValid || !regNumber) return false;

    setIsSearching(true);
    try {
      const found = await lookupComplaintVehicleAsync(regNumber);
      if (found) {
        form.clearErrors('registrationNumber');
        return true;
      }
      form.setError('registrationNumber', {
        message: labels.vehicleNotFound ?? 'Fordonet hittades inte i systemet',
      });
      return false;
    } catch {
      form.setError('registrationNumber', {
        message: labels.vehicleSearchError ?? 'Ett fel uppstod vid sökning',
      });
      return false;
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const formWithSearch = form as typeof form & { handleSearch?: () => Promise<boolean> };
    formWithSearch.handleSearch = handleSearch;
  }, [validationResult.isValid, form.getValues('registrationNumber')]);

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="registrationNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel className={baseText}>{labels.registrationNumber}</FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  placeholder="ABC123"
                  className={`${isDark ? "bg-black/20 border-white/20 text-white" : "bg-white border-gray-200 text-black"} placeholder:text-gray-400 py-1.5 text-sm pr-10 ${validationResult.isValid && field.value
                    ? 'border-green-500'
                    : field.value && !validationResult.isValid
                      ? 'border-red-500'
                      : ''
                    }`}
                  maxLength={7}
                  {...field}
                  value={field.value.toUpperCase()}
                />
                {(isValidating || isSearching || isLookingUp) && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  </div>
                )}
                {validationResult.isValid && field.value && !isValidating && !isSearching && !isLookingUp && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  </div>
                )}
                {!validationResult.isValid && field.value && !isValidating && validationResult.error && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  </div>
                )}
              </div>
            </FormControl>
            <FormDescription className={`${subtleText} text-xs`}>
              {labels.registrationStep}
            </FormDescription>
            <FormMessage />

            {field.value && !isValidating && (
              <div className="mt-2">
                {validationResult.isValid ? (
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{labels.registrationValidFormat ?? 'Korrekt format'}</span>
                  </div>
                ) : validationResult.error ? (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{validationResult.error}</span>
                  </div>
                ) : null}
              </div>
            )}
          </FormItem>
        )}
      />
    </div>
  );
};
